import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API, { SERVER_URL } from '../../api/axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area, Cell
} from 'recharts';
import { LuBan, LuBanknote, LuChartLine, LuCircleCheck, LuClipboardList, LuCreditCard, LuEye, LuGift, LuHourglass, LuInfo, LuMinus, LuPackage, LuPackageX, LuPlus, LuReceipt, LuShoppingCart, LuSmartphone, LuTrash2, LuUsers, LuWallet } from 'react-icons/lu';
import AppLayout from '../../components/layout/AppLayout';
import { Page, PageHeader, Card, StatCard, Grid, Badge, Tabs, EmptyState, Loader, Button } from '../../components/ui';
import { C, ROLES, fmt, fmtK, fmtF } from '../../theme';
import { S } from '../../components/pageStyles';
import CommandesEnLigne from '../../components/commandes/CommandesEnLigne';
import ApprovisionnementIA from '../../components/appro/ApprovisionnementIA';

const COULEURS_SERVEURS = ['#2563EB', '#EA580C', '#059669', '#7C3AED', '#0E7490', '#DC2626'];

const MODES_PAIEMENT = {
  especes: { label: 'Espèces', icon: <LuBanknote aria-hidden /> },
  mobile_money: { label: 'Mobile Money', icon: <LuSmartphone aria-hidden /> },
  credit: { label: 'Crédit', icon: <LuClipboardList aria-hidden /> },
};

const PAR_PAGE = 10;

export default function DashboardGerant() {
  const { utilisateur } = useAuth();
  const [onglet, setOnglet] = useState('dashboard');
  const [chargement, setChargement] = useState(true);
  const [message, setMessage] = useState('');
  const [etablissement, setEtablissement] = useState(null);
  const [chargementDetail, setChargementDetail] = useState(false);

  const [caData, setCaData] = useState([]);
  const [serveurs, setServeurs] = useState([]);
  const [produits, setProduits] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [kits, setKits] = useState([]);
  const [ventes, setVentes] = useState([]);
  const [venteDetail, setVenteDetail] = useState(null);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [rechercheVente, setRechercheVente] = useState('');
  const [pageVente, setPageVente] = useState(1);

  const [vueCaisse, setVueCaisse] = useState('historique');
  const [panier, setPanier] = useState([]);
  const [modePaiement, setModePaiement] = useState('especes');
  const [note, setNote] = useState('');
  const [ongletCaisse, setOngletCaisse] = useState('produits');
  const [rechercheArticle, setRechercheArticle] = useState('');
  const [chargementVente, setChargementVente] = useState(false);

  const [modalAppro, setModalAppro] = useState(null);
  const [modalPerte, setModalPerte] = useState(null);
  const [qteAppro, setQteAppro] = useState('');
  const [motifAppro, setMotifAppro] = useState('');
  const [qtePerte, setQtePerte] = useState('');
  const [motifPerte, setMotifPerte] = useState('');
  const [rechercheProduit, setRechercheProduit] = useState('');
  const [resumeEnLigne, setResumeEnLigne] = useState(null);

  const chargerDonnees = async () => {
    try {
      const etabId = utilisateur?.etablissement_id;
      const param = etabId ? '&etablissement_id=' + etabId : '';
      const [etabRes, caRes, serveursRes, alertesRes, produitsRes, ventesRes, kitsRes] = await Promise.all([
        API.get('/etablissements/' + etabId),
        API.get('/rapports/chiffre-affaires?periode=mois' + param),
        API.get('/rapports/serveurs?periode=mois' + param),
        API.get('/rapports/stock'),
        API.get('/produits'),
        API.get('/ventes'),
        API.get('/kits'),
      ]);
      setEtablissement(etabRes.data.etablissement);
      setCaData(caRes.data.data || []);
      setServeurs(serveursRes.data.serveurs || []);
      setAlertes(alertesRes.data.produits || []);
      setProduits(produitsRes.data.produits || []);
      setVentes(ventesRes.data.ventes || []);
      setKits(kitsRes.data.kits || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { chargerDonnees(); }, []);

  // Badge "Commandes en ligne" : nouvelles commandes + messages non lus
  useEffect(() => {
    const majResume = () => API.get('/commandes-en-ligne/resume').then(r => setResumeEnLigne(r.data)).catch(() => {});
    majResume();
    const t = setInterval(majResume, 30000);
    return () => clearInterval(t);
  }, []);

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const caAujourdhui = ventes
    .filter(v => v.statut === 'validee' &&
      new Date(v.created_at).toDateString() === new Date().toDateString())
    .reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);

  const handleApprovisionner = async () => {
    if (!qteAppro || qteAppro <= 0) return;
    try {
      await API.post('/produits/' + modalAppro.id + '/approvisionner', {
        quantite: parseInt(qteAppro), motif: motifAppro
      });
      showMessage('✅ Stock approvisionné !');
      setModalAppro(null); setQteAppro(''); setMotifAppro('');
      chargerDonnees();
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handlePerte = async () => {
    if (!qtePerte || qtePerte <= 0) return;
    try {
      await API.post('/produits/' + modalPerte.id + '/perte', {
        quantite: parseInt(qtePerte), motif: motifPerte
      });
      showMessage('✅ Perte enregistrée !');
      setModalPerte(null); setQtePerte(''); setMotifPerte('');
      chargerDonnees();
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const ajouterAuPanier = (article, type) => {
    const existant = panier.find(p => p.id === article.id && p.type === type);
    if (existant) {
      setPanier(panier.map(p =>
        p.id === article.id && p.type === type ? { ...p, quantite: p.quantite + 1 } : p
      ));
    } else {
      setPanier([...panier, {
        id: article.id, type, nom: article.nom,
        prix: parseFloat(article.prix || article.prix_kit),
        quantite: 1, unite: article.unite || 'Unité'
      }]);
    }
  };

  const modifierQte = (id, type, delta) => {
    setPanier(panier.map(p => {
      if (p.id === id && p.type === type) {
        const nq = p.quantite + delta;
        if (nq <= 0) return null;
        return { ...p, quantite: nq };
      }
      return p;
    }).filter(Boolean));
  };

  const totalPanier = panier.reduce((t, p) => t + p.prix * p.quantite, 0);

  const validerVente = async () => {
    if (panier.length === 0) return;
    setChargementVente(true);
    try {
      const prods = panier.filter(p => p.type === 'produit').map(p => ({ produit_id: p.id, quantite: p.quantite }));
      const kitsV = panier.filter(p => p.type === 'kit').map(p => ({ kit_id: p.id, quantite: p.quantite }));
      await API.post('/ventes', {
        etablissement_id: utilisateur?.etablissement_id,
        mode_paiement: modePaiement,
        produits: prods, kits: kitsV, note
      });
      showMessage('✅ Vente enregistrée !');
      setPanier([]); setNote(''); setModePaiement('especes');
      setVueCaisse('historique');
      chargerDonnees();
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    } finally {
      setChargementVente(false);
    }
  };

  const handleAnnulerVente = async (vente) => {
    if (!window.confirm('Confirmer l\'annulation de cette vente ?')) return;
    try {
      await API.patch('/ventes/' + vente.id + '/annuler', {
        motif_annulation: 'Annulée par le gérant'
      });
      showMessage('✅ Vente annulée !');
      setVenteDetail(null);
      setVentes(prev => prev.filter(v => v.id !== vente.id));
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  // Ouvrir détail avec chargement des articles
  const ouvrirDetailVente = async (vente) => {
    setVenteDetail(vente);
    setChargementDetail(true);
    try {
      const res = await API.get('/ventes/' + vente.id);
      setVenteDetail({ ...vente, details: res.data.details || [] });
    } catch (err) {
      console.error(err);
    } finally {
      setChargementDetail(false);
    }
  };

  const ventesVisibles = ventes.filter(v => {
    const matchStatut = filtreStatut === 'tous'
      ? v.statut !== 'annulee'
      : v.statut === filtreStatut;
    const matchRecherche =
      (v.numero_facture || '').toLowerCase().includes(rechercheVente.toLowerCase()) ||
      ((v.serveur_prenom || '') + ' ' + (v.serveur_nom || '')).toLowerCase().includes(rechercheVente.toLowerCase());
    return matchStatut && matchRecherche;
  });

  const totalPages = Math.ceil(ventesVisibles.length / PAR_PAGE);
  const ventesPage = ventesVisibles.slice((pageVente - 1) * PAR_PAGE, pageVente * PAR_PAGE);

  const totalCA = ventesVisibles.filter(v => v.statut === 'validee')
    .reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);

  const articlesFiltres = ongletCaisse === 'produits'
    ? produits.filter(p => p.nom.toLowerCase().includes(rechercheArticle.toLowerCase()) && p.quantite > 0)
    : kits.filter(k => k.nom.toLowerCase().includes(rechercheArticle.toLowerCase()) && k.actif);

  const produitsFiltres = produits.filter(p =>
    p.nom.toLowerCase().includes(rechercheProduit.toLowerCase())
  );

  if (chargement) return <Loader />;

  const aujourdhui = new Date().toDateString();
  const ventesJour = ventes.filter(v => v.statut === 'validee' && new Date(v.created_at).toDateString() === aujourdhui);
  const totalMode = mode => ventesJour.filter(v => v.mode_paiement === mode).reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);
  const ongletCourant = ROLES.gerant.nav.find(n => n.id === onglet);
  const succes = message.startsWith('✅');

  return (
    <AppLayout
      role="gerant"
      activeId={onglet}
      onSelect={setOnglet}
      subtitle={etablissement?.nom}
      badges={{ produits: alertes.length, commandes: (Number(resumeEnLigne?.nouvelles) || 0) + (Number(resumeEnLigne?.non_lus) || 0) }}
    >
      <Page>
        {message && (
          <div role="status" style={{
            position: 'fixed', top: 16, right: 16, zIndex: 1100, maxWidth: 'calc(100vw - 32px)',
            padding: '12px 18px', borderRadius: 12, fontWeight: 600, fontSize: 14,
            boxShadow: '0 8px 24px rgba(15,23,42,0.15)', animation: 'sp-fade-up .2s ease both',
            backgroundColor: succes ? C.successLight : C.dangerLight,
            color: succes ? '#047857' : '#B91C1C',
          }}>{message.replace(/^(✅|❌)\s*/, '')}</div>
        )}

        <PageHeader
          title={ongletCourant?.label}
          subtitle={`${etablissement?.nom ? etablissement.nom + ' · ' : ''}${new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
          actions={onglet === 'ventes' && (
            <Tabs
              tabs={[
                { id: 'historique', label: 'Historique', icon: LuReceipt },
                { id: 'caisse', label: 'Nouvelle vente', icon: LuPlus },
              ]}
              value={vueCaisse}
              onChange={setVueCaisse}
            />
          )}
        />

        {/* ── COMMANDES EN LIGNE (WhatsApp / Messenger + agent IA) ── */}
        {onglet === 'commandes' && <CommandesEnLigne onResume={setResumeEnLigne} />}

        {/* ── APPROVISIONNEMENT PAR FACTURE (agent IA) ── */}
        {onglet === 'appro' && <ApprovisionnementIA etablissementId={utilisateur?.etablissement_id} onStockChange={chargerDonnees} />}

        {/* ── DASHBOARD ── */}
        {onglet === 'dashboard' && (
          <>
            <Grid min={200} style={{ marginBottom: 20 }}>
              <StatCard icon={LuWallet}     label="Revenus du jour"   value={`${fmtF(caAujourdhui)}`} hint={`${ventesJour.length} vente${ventesJour.length > 1 ? 's' : ''}`} color={ROLES.gerant.accent} />
              <StatCard icon={LuBanknote}   label="Espèces"          value={`${fmtF(totalMode('especes'))}`} color={C.success} />
              <StatCard icon={LuSmartphone} label="Mobile Money"     value={`${fmtF(totalMode('mobile_money'))}`} color={C.purple} />
            </Grid>
            <Grid min={200} gap={12} style={{ marginBottom: 20 }}>
              <StatCard icon={LuPackage}  label="Produits"      value={produits.length} color="#0E7490" />
              <StatCard icon={LuPackageX} label="Alertes stock" value={alertes.length}  color={alertes.length ? C.danger : C.textMuted} />
              <StatCard icon={LuUsers}    label="Serveurs actifs ce mois" value={serveurs.length} color={C.warn} />
            </Grid>

            <Card title="Évolution des revenus ce mois" style={{ marginBottom: 20 }}>
              {caData.length === 0 ? <EmptyState icon={LuChartLine} title="Aucune vente ce mois" /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={caData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gerantCa" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={ROLES.gerant.accent} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={ROLES.gerant.accent} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={C.border} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: C.textMuted }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: C.textMuted }} axisLine={false} tickLine={false} width={48} />
                    <Tooltip formatter={v => [`${fmtF(v)}`, 'Revenus']} contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="ca" stroke={ROLES.gerant.accent} strokeWidth={2.5} fill="url(#gerantCa)" dot={{ r: 3, fill: ROLES.gerant.accent, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Grid min={340}>
              <Card title="Performance des serveurs">
                {serveurs.length === 0 ? <EmptyState icon={LuUsers} title="Aucune vente ce mois" /> : (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={serveurs} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={C.border} />
                        <XAxis dataKey="nom" tick={{ fontSize: 11, fill: C.textMuted }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: C.textMuted }} axisLine={false} tickLine={false} width={48} />
                        <Tooltip formatter={v => [`${fmtF(v)}`, 'Revenus']} contentStyle={tooltipStyle} cursor={{ fill: C.separator }} />
                        <Bar dataKey="ca" radius={[6, 6, 0, 0]} maxBarSize={44}>
                          {serveurs.map((_, i) => (
                            <Cell key={i} fill={COULEURS_SERVEURS[i % COULEURS_SERVEURS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 12 }}>
                      {serveurs.map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: COULEURS_SERVEURS[i % COULEURS_SERVEURS.length], flexShrink: 0 }} />
                          <span style={{ color: C.textSub }}>{s.nom} · <strong style={{ color: C.text }}>{fmtF(s.ca)}</strong></span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>

              <Card
                title="Alertes stock"
                action={alertes.length > 0 && <Button variant="secondary" onClick={() => setOnglet('produits')} style={{ minHeight: 32, fontSize: 13 }}>Approvisionner</Button>}
              >
                {alertes.length === 0 ? (
                  <EmptyState icon={LuCircleCheck} title="Tous les stocks sont OK" />
                ) : (
                  alertes.slice(0, 6).map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: `1px solid ${C.separator}` }}>
                      <span style={{ fontWeight: 600, color: C.text }}>{p.nom}</span>
                      <Badge tone={p.quantite === 0 ? 'danger' : 'warn'}>
                        {p.quantite === 0 ? 'Rupture' : `${p.quantite} ${p.unite}`}
                      </Badge>
                    </div>
                  ))
                )}
              </Card>
            </Grid>
          </>
        )}

        {/* ── PRODUITS ── */}
        {onglet === 'produits' && (
          <>
            <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: '#1565C0', fontSize: 13, fontWeight: 700 }}>
              <LuInfo aria-hidden style={{ verticalAlign: '-0.125em' }} /> En tant que gérant, vous pouvez gérer les stocks mais pas modifier les prix.
            </div>
            <div style={styles.filtres}>
              <input type="text" placeholder="Rechercher un produit..."
                value={rechercheProduit} onChange={(e) => setRechercheProduit(e.target.value)}
                style={styles.rechercheInput} />
              <span style={{ color: '#64748B', fontSize: 13 }}>{produitsFiltres.length} produit(s)</span>
            </div>
            <div style={styles.section}>
              {produitsFiltres.length === 0 ? (
                <div style={styles.vide}><div style={{ fontSize: 48 }}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /></div><p>Aucun produit trouvé</p></div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      {['Image', 'Produit', 'Prix', 'Stock', 'Seuil', 'Statut', 'Actions'].map((h, i) => (
                        <th key={i} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {produitsFiltres.map((p, i) => {
                      const statut = p.quantite === 0 ? 'rupture' : p.quantite <= p.seuil_alerte ? 'alerte' : 'normal';
                      const sc = {
                        rupture: { label: 'Rupture', couleur: '#DC2626', bg: '#FEF2F2' },
                        alerte: { label: 'Alerte', couleur: '#EA580C', bg: '#FFF7ED' },
                        normal: { label: 'Normal', couleur: '#059669', bg: '#ECFDF5' },
                      };
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                          <td style={styles.td}>
                            {p.image_principale ? (
                              <img src={SERVER_URL + '/uploads/produits/' + p.image_principale.replace(/\\/g, '/').split('/').pop()}
                                alt="produit" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                            )}
                          </td>
                          <td style={styles.td}>
                            <div style={{ fontWeight: 700 }}>{p.nom}</div>
                            <div style={{ color: '#64748B', fontSize: 11 }}>{p.unite}</div>
                          </td>
                          <td style={styles.td}>
                            <span style={{ fontWeight: 700, color: '#64748B' }}>{fmtF(parseInt(p.prix))}</span>
                            <div style={{ color: '#94A3B8', fontSize: 10 }}>Non modifiable</div>
                          </td>
                          <td style={styles.td}>
                            <span style={{ fontWeight: 700, fontSize: 18, color: sc[statut].couleur }}>{p.quantite}</span>
                            <span style={{ color: '#64748B', fontSize: 11 }}>{' ' + p.unite}</span>
                          </td>
                          <td style={styles.td}>{p.seuil_alerte}</td>
                          <td style={styles.td}>
                            <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: sc[statut].bg, color: sc[statut].couleur }}>
                              {sc[statut].label}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => { setModalAppro(p); setQteAppro(''); setMotifAppro(''); }} style={styles.btnAppro} aria-label="Approvisionner" title="Approvisionner"><LuPlus aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                              <button onClick={() => { setModalPerte(p); setQtePerte(''); setMotifPerte(''); }} style={styles.btnPerte} aria-label="Enregistrer une perte" title="Enregistrer une perte"><LuMinus aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ── KITS ── */}
        {onglet === 'kits' && (
          <>
            <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: '#1565C0', fontSize: 13, fontWeight: 700 }}>
              <LuInfo aria-hidden style={{ verticalAlign: '-0.125em' }} /> En tant que gérant, vous pouvez voir les kits mais pas modifier les prix.
            </div>
            {kits.length === 0 ? (
              <div style={{ ...styles.section, textAlign: 'center', padding: 60 }}>
                <div style={{ fontSize: 60 }}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                <p style={{ fontWeight: 700, fontSize: 16, marginTop: 12 }}>Aucun kit</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {kits.map((kit, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 16, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', opacity: kit.actif ? 1 : 0.6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{kit.nom}</div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#2563EB' }}>{fmtF(parseInt(kit.prix_kit))}</div>
                      </div>
                      {!kit.actif && <span style={{ fontSize: 11, color: '#64748B', background: '#F1F5F9', padding: '3px 8px', borderRadius: 12 }}>Inactif</span>}
                    </div>
                    {kit.description && <p style={{ color: '#64748B', fontSize: 13, marginBottom: 12, fontStyle: 'italic' }}>{kit.description}</p>}
                    <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 12 }}>
                      <div style={{ color: '#64748B', fontSize: 11, marginBottom: 6 }}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Composition</div>
                      {(kit.produits || []).map((p, j) => (
                        <div key={j} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12, borderBottom: '1px solid #EFEFEF' }}>
                          <span>{p.nom}</span>
                          <span style={{ background: '#EFF6FF', color: '#2563EB', padding: '1px 8px', borderRadius: 10, fontWeight: 700 }}>x{p.qte_kit || p.quantite || 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── VENTES HISTORIQUE ── */}
        {onglet === 'ventes' && vueCaisse === 'historique' && (
          <>
            <div style={styles.statsGrid}>
              {[
                { icon: <LuWallet aria-hidden />, label: 'Revenus validés', valeur: fmtF(totalCA), couleur: '#2563EB', bg: '#EFF6FF' },
                { icon: <LuCircleCheck aria-hidden />, label: 'Validées', valeur: ventes.filter(v => v.statut === 'validee').length, couleur: '#059669', bg: '#ECFDF5' },
              
                { icon: <LuSmartphone aria-hidden />, label: 'Mobile Money', valeur: ventes.filter(v => v.mode_paiement === 'mobile_money' && v.statut === 'validee').length, couleur: '#7C3AED', bg: '#F5F3FF' },
              ].map((stat, i) => (
                <StatCard key={i} icon={stat.icon} label={stat.label} value={stat.valeur} color={stat.couleur} />
              ))}
            </div>

            <div style={styles.filtres}>
              <input type="text"
                placeholder="Rechercher par facture ou serveur..."
                value={rechercheVente}
                onChange={(e) => { setRechercheVente(e.target.value); setPageVente(1); }}
                style={styles.rechercheInput}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { val: 'tous', label: 'Validées' },
                  { val: 'annulee', label: 'Annulées' }
                ].map(f => (
                  <button key={f.val} onClick={() => { setFiltreStatut(f.val); setPageVente(1); }} style={{
                    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 12,
                    background: filtreStatut === f.val ? '#2563EB' : '#F8FAFC',
                    color: filtreStatut === f.val ? 'white' : '#475569'
                  }}>{f.label}</button>
                ))}
              </div>
              <span style={{ color: '#64748B', fontSize: 13 }}>{ventesVisibles.length} résultat(s)</span>
            </div>

            <div style={styles.section}>
              {ventesPage.length === 0 ? (
                <div style={styles.vide}><div style={{ fontSize: 48 }}><LuShoppingCart aria-hidden style={{ verticalAlign: '-0.125em' }} /></div><p>Aucune vente</p></div>
              ) : (
                <>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.tableHeader}>
                        {['Facture', 'Serveur', 'Montant', 'Paiement', 'Statut', 'Date', 'Actions'].map((h, i) => (
                          <th key={i} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ventesPage.map((v, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                          <td style={styles.td}>
                            <span style={{ fontFamily: 'monospace', color: '#2563EB', fontWeight: 700 }}>
                              {v.numero_facture || '#' + v.id}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>
                              {(v.serveur_prenom || '') + ' ' + (v.serveur_nom || '')}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={{ fontWeight: 700, color: '#2563EB', fontSize: 15 }}>
                              {fmtF(parseInt(v.montant_total))}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{MODES_PAIEMENT[v.mode_paiement]?.icon} {MODES_PAIEMENT[v.mode_paiement]?.label}</span>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                              background: v.statut === 'validee' ? '#ECFDF5' : '#FEF2F2',
                              color: v.statut === 'validee' ? '#059669' : '#DC2626'
                            }}>
                              {v.statut === 'validee' ? 'Validée' : 'Annulée'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={{ fontSize: 12 }}>{new Date(v.created_at).toLocaleDateString('fr-FR')}</div>
                            <div style={{ color: '#64748B', fontSize: 11 }}>
                              {new Date(v.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => ouvrirDetailVente(v)} style={styles.btnVoir} aria-label="Voir le détail" title="Voir le détail"><LuEye aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                              {v.statut === 'validee' && (
                                <button onClick={() => handleAnnulerVente(v)} style={styles.btnAnnulerBtn} aria-label="Annuler la vente" title="Annuler la vente"><LuBan aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* PAGINATION */}
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
                      <button onClick={() => setPageVente(p => Math.max(1, p - 1))} disabled={pageVente === 1} style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none',
                        cursor: pageVente === 1 ? 'not-allowed' : 'pointer',
                        background: pageVente === 1 ? '#F8FAFC' : '#2563EB',
                        color: pageVente === 1 ? '#94A3B8' : 'white', fontWeight: 700
                      }}>← Préc.</button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - pageVente) <= 1)
                        .map((p, i, arr) => (
                          <span key={p} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color: '#64748B' }}>...</span>}
                            <button onClick={() => setPageVente(p)} style={{
                              padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                              background: pageVente === p ? '#2563EB' : '#F8FAFC',
                              color: pageVente === p ? 'white' : '#475569', fontWeight: 700, minWidth: 36
                            }}>{p}</button>
                          </span>
                        ))}

                      <button onClick={() => setPageVente(p => Math.min(totalPages, p + 1))} disabled={pageVente === totalPages} style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none',
                        cursor: pageVente === totalPages ? 'not-allowed' : 'pointer',
                        background: pageVente === totalPages ? '#F8FAFC' : '#2563EB',
                        color: pageVente === totalPages ? '#94A3B8' : 'white', fontWeight: 700
                      }}>Suiv. →</button>

                      <span style={{ color: '#64748B', fontSize: 13 }}>Page {pageVente}/{totalPages}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* ── CAISSE GÉRANT ── */}
        {onglet === 'ventes' && vueCaisse === 'caisse' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20 }}>
            <div>
              <div style={{ background: 'white', borderRadius: 12, padding: '16px 20px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {[{ id: 'produits', label: 'Produits' }, { id: 'kits', label: 'Kits' }].map(o => (
                    <button key={o.id} onClick={() => setOngletCaisse(o.id)} style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: 13,
                      background: ongletCaisse === o.id ? '#2563EB' : '#F8FAFC',
                      color: ongletCaisse === o.id ? 'white' : '#475569'
                    }}>{o.label}</button>
                  ))}
                </div>
                <input type="text"
                  placeholder={'Rechercher ' + (ongletCaisse === 'produits' ? 'un produit' : 'un kit') + '...'}
                  value={rechercheArticle} onChange={(e) => setRechercheArticle(e.target.value)}
                  style={{ ...styles.rechercheInput, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {articlesFiltres.map((article, i) => (
                  <div key={i} onClick={() => ajouterAuPanier(article, ongletCaisse === 'produits' ? 'produit' : 'kit')}
                    style={{ background: 'white', borderRadius: 12, padding: 14, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center', border: '2px solid transparent' }}
                    onMouseEnter={e => e.currentTarget.style.border = '2px solid #2563EB'}
                    onMouseLeave={e => e.currentTarget.style.border = '2px solid transparent'}
                  >
                    <div style={{ marginBottom: 8 }}>
                      {ongletCaisse === 'produits' && article.image_principale ? (
                        <img src={SERVER_URL + '/uploads/produits/' + article.image_principale.replace(/\\/g, '/').split('/').pop()}
                          alt="produit" style={{ width: 70, height: 70, borderRadius: 8, objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 70, height: 70, borderRadius: 8, margin: '0 auto', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>
                          {ongletCaisse === 'produits' ? <LuPackage aria-hidden /> : <LuGift aria-hidden />}
                        </div>
                      )}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{article.nom}</div>
                    <div style={{ color: '#2563EB', fontWeight: 700, fontSize: 14 }}>
                      {fmtF(parseInt(article.prix || article.prix_kit))}
                    </div>
                    {ongletCaisse === 'produits' && (
                      <div style={{ color: article.quantite <= article.seuil_alerte ? '#DC2626' : '#059669', fontSize: 10, marginTop: 2 }}>
                        Stock: {article.quantite}
                      </div>
                    )}
                  </div>
                ))}
                {articlesFiltres.length === 0 && (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#64748B' }}>Aucun article disponible</div>
                )}
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', height: 'fit-content', position: 'sticky', top: 24 }}>
              <h3 style={{ margin: '0 0 16px', color: '#0F172A' }}><LuReceipt aria-hidden style={{ verticalAlign: '-0.125em' }} /> Panier ({panier.length})</h3>
              {panier.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#64748B' }}>
                  <div style={{ fontSize: 40 }}><LuShoppingCart aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p style={{ fontSize: 13 }}>Cliquez sur un article</p>
                </div>
              ) : (
                <>
                  <div style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
                    {panier.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{item.nom}</div>
                          <div style={{ color: '#2563EB', fontSize: 12 }}>{fmtF(parseInt(item.prix))}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <button onClick={() => modifierQte(item.id, item.type, -1)} style={styles.btnQte}>−</button>
                          <span style={{ fontWeight: 700, fontSize: 14, minWidth: 20, textAlign: 'center' }}>{item.quantite}</span>
                          <button onClick={() => modifierQte(item.id, item.type, 1)} style={styles.btnQte}>+</button>
                          <button onClick={() => setPanier(panier.filter(p => !(p.id === item.id && p.type === item.type)))}
                            style={{ ...styles.btnQte, background: '#FEF2F2', color: '#DC2626' }}>✕</button>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#2563EB', minWidth: 55, textAlign: 'right' }}>
                          {fmtF((parseInt(item.prix) * item.quantite))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '12px 16px', marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18 }}>
                      <span>Total</span>
                      <span style={{ color: '#2563EB' }}>{fmtF(totalPanier)}</span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#334155' }}><LuCreditCard aria-hidden style={{ verticalAlign: '-0.125em' }} /> Paiement</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {Object.entries(MODES_PAIEMENT).map(([key, val]) => (
                        <button key={key} onClick={() => setModePaiement(key)} style={{
                          flex: 1, padding: '7px 4px', borderRadius: 8, border: 'none',
                          cursor: 'pointer', fontSize: 11, fontWeight: 700,
                          background: modePaiement === key ? 'var(--accent)' : '#F1F5F9',
                          color: modePaiement === key ? 'white' : '#475569'
                        }}>{val.icon} {val.label}</button>
                      ))}
                    </div>
                  </div>
                  <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Note..." style={{ ...styles.rechercheInput, width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />
                  <button onClick={validerVente} disabled={chargementVente} style={{
                    width: '100%', padding: '14px', border: 'none', borderRadius: 10,
                    background: chargementVente ? '#ccc' : 'var(--accent)',
                    color: 'white', fontWeight: 700, fontSize: 15, cursor: chargementVente ? 'not-allowed' : 'pointer'
                  }}>
                    {chargementVente ? '...' : 'Valider ' + fmtF(totalPanier)}
                  </button>
                  <button onClick={() => setPanier([])} style={{
                    width: '100%', marginTop: 8, padding: '9px', border: '1px solid #E2E8F0',
                    borderRadius: 10, background: 'white', color: '#DC2626', cursor: 'pointer', fontSize: 13
                  }}><LuTrash2 aria-hidden style={{ verticalAlign: '-0.125em' }} /> Vider</button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── ÉQUIPE ── */}
        {onglet === 'equipe' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitre}>Mon Équipe ({serveurs.length} serveur(s))</h2>
            {serveurs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>
                <div style={{ fontSize: 48 }}><LuUsers aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                <p>Aucun serveur ce mois</p>
              </div>
            ) : (
              serveurs.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, borderRadius: 12, marginBottom: 12, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: COULEURS_SERVEURS[i % COULEURS_SERVEURS.length],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 700, fontSize: 16
                  }}>{(s.nom || '?')[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{s.nom}</div>
                    <div style={{ color: '#64748B', fontSize: 12 }}>
                      {s.nombre_ventes + ' ventes • ' + fmt(parseInt(s.ca || 0)) + ' FCFA ce mois'}
                    </div>
                    <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, marginTop: 8 }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        background: COULEURS_SERVEURS[i % COULEURS_SERVEURS.length],
                        width: Math.min((parseFloat(s.ca || 0) / Math.max(...serveurs.map(sv => parseFloat(sv.ca || 1)))) * 100, 100) + '%'
                      }} />
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, color: COULEURS_SERVEURS[i % COULEURS_SERVEURS.length], fontSize: 16 }}>
                      {fmtF(s.nombre_ventes > 0 ? s.ca / s.nombre_ventes : 0)}
                    </div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>panier moy.</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      {/* MODAL APPRO */}
      {modalAppro && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={{ color: '#059669', marginBottom: 8 }}><LuPlus aria-hidden style={{ verticalAlign: '-0.125em' }} /> Approvisionner</h3>
            <p style={{ fontWeight: 700, marginBottom: 4 }}>{modalAppro.nom}</p>
            <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>
              Stock actuel : <strong style={{ color: '#059669' }}>{modalAppro.quantite} {modalAppro.unite}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input type="number" min="1" value={qteAppro} onChange={(e) => setQteAppro(e.target.value)} style={styles.inputModal} placeholder="Quantité à ajouter *" />
              <input type="text" value={motifAppro} onChange={(e) => setMotifAppro(e.target.value)} style={styles.inputModal} placeholder="Motif (livraison...)" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => setModalAppro(null)} style={styles.btnAnnuler}>Annuler</button>
              <button onClick={handleApprovisionner} style={{ ...styles.btnValider, background: '#059669' }}><LuPlus aria-hidden style={{ verticalAlign: '-0.125em' }} /> Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PERTE */}
      {modalPerte && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={{ color: '#DC2626', marginBottom: 8 }}><LuMinus aria-hidden style={{ verticalAlign: '-0.125em' }} /> Enregistrer une Perte</h3>
            <p style={{ fontWeight: 700, marginBottom: 4 }}>{modalPerte.nom}</p>
            <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>
              Stock actuel : <strong style={{ color: '#DC2626' }}>{modalPerte.quantite} {modalPerte.unite}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input type="number" min="1" max={modalPerte.quantite} value={qtePerte} onChange={(e) => setQtePerte(e.target.value)} style={styles.inputModal} placeholder="Quantité perdue *" />
              <input type="text" value={motifPerte} onChange={(e) => setMotifPerte(e.target.value)} style={styles.inputModal} placeholder="Motif * (périmé, casse...)" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => setModalPerte(null)} style={styles.btnAnnuler}>Annuler</button>
              <button onClick={handlePerte} style={{ ...styles.btnValider, background: '#DC2626' }}><LuMinus aria-hidden style={{ verticalAlign: '-0.125em' }} /> Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DÉTAIL VENTE */}
      {venteDetail && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#0F172A' }}><LuReceipt aria-hidden style={{ verticalAlign: '-0.125em' }} /> {venteDetail.numero_facture || '#' + venteDetail.id}</h3>
              <button onClick={() => setVenteDetail(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            {/* INFOS */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              {[
                { label: 'Serveur', valeur: (venteDetail.serveur_prenom || '') + ' ' + (venteDetail.serveur_nom || '') },
                { label: 'Paiement', valeur: MODES_PAIEMENT[venteDetail.mode_paiement]?.label },
                { label: 'Statut', valeur: venteDetail.statut === 'validee' ? 'Validée' : 'Annulée' },
                { label: 'Date', valeur: new Date(venteDetail.created_at).toLocaleString('fr-FR') },
              ].map((info, i) => (
                <div key={i} style={{ background: '#F8FAFC', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ color: '#64748B', fontSize: 11 }}>{info.label}</div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{info.valeur}</div>
                </div>
              ))}
            </div>

            {/* DÉTAILS ARTICLES */}
            <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: 12, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A', marginBottom: 8 }}>
                <LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Articles
              </div>
              {chargementDetail ? (
                <div style={{ textAlign: 'center', padding: 16, color: '#64748B' }}><LuHourglass aria-hidden style={{ verticalAlign: '-0.125em' }} /> Chargement...</div>
              ) : (venteDetail.details || []).length === 0 ? (
                <div style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: 12 }}>
                  Aucun détail disponible
                </div>
              ) : (
                (venteDetail.details || []).map((d, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 0', borderBottom: '1px solid #F1F5F9', fontSize: 13
                  }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{d.produit_nom || d.kit_nom || '—'}</div>
                      <div style={{ color: '#64748B', fontSize: 11 }}>
                        {fmtF(d.prix_unitaire) + ' × ' + d.quantite}
                      </div>
                    </div>
                    <span style={{ fontWeight: 700, color: '#2563EB' }}>
                      {fmtF(parseInt(d.sous_total))}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* TOTAL */}
            <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '12px 16px', fontWeight: 700, fontSize: 18, display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span>Total</span>
              <span style={{ color: '#2563EB' }}>{fmtF(parseInt(venteDetail.montant_total))}</span>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setVenteDetail(null)} style={styles.btnAnnuler}>Fermer</button>
              {venteDetail.statut === 'validee' && (
                <button onClick={() => handleAnnulerVente(venteDetail)} style={{ ...styles.btnValider, background: '#DC2626' }}>
                  <LuBan aria-hidden style={{ verticalAlign: '-0.125em' }} /> Annuler la vente
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </Page>
    </AppLayout>
  );
}

const styles = {
  statsGrid: { ...S.statsGrid, marginBottom: 20 },
  statCard: S.statCard,
  filtres: { ...S.filtres, marginBottom: 16 },
  rechercheInput: { ...S.rechercheInput },
  section: { ...S.section, marginBottom: 20 },
  sectionTitre: S.sectionTitre,
  vide: S.vide,
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
  btnAppro: S.btnAppro,
  btnPerte: S.btnPerte,
  btnVoir: S.btnVoir,
  btnAnnulerBtn: S.btnAnnulerBtn,
  btnQte: { width: 26, height: 26, borderRadius: 6, border: 'none', background: '#F8FAFC', cursor: 'pointer', fontWeight: 700, fontSize: 15 },
  overlay: S.overlay,
  modal: { ...S.modal, width: 'min(460px, 100%)' },
  inputModal: { padding: '12px 16px', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  btnAnnuler: { ...S.btnAnnuler, flex: 1 },
  btnValider: { ...S.btnValider, flex: 1 },
};
const tooltipStyle = { borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, boxShadow: '0 4px 12px rgba(15,23,42,0.08)' };
